import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Génère un défi mathématique simple
const generateChallenge = () => {
  const operations = ['+', '-', 'x'];
  const operation = operations[Math.floor(Math.random() * operations.length)];
  let num1, num2, answer;
  
  switch (operation) {
    case '+':
      num1 = Math.floor(Math.random() * 20) + 1;
      num2 = Math.floor(Math.random() * 20) + 1;
      answer = num1 + num2;
      break;
    case '-':
      num1 = Math.floor(Math.random() * 20) + 10;
      num2 = Math.floor(Math.random() * 10) + 1;
      answer = num1 - num2;
      break;
    case 'x':
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
      answer = num1 * num2;
      break;
    default:
      num1 = 5;
      num2 = 3;
      answer = 8;
  }
  
  return {
    question: `${num1} ${operation} ${num2} = ?`,
    answer: answer,
    token: btoa(JSON.stringify({ a: answer, t: Date.now(), s: Math.random().toString(36).substring(7) }))
  };
};

export default function CaptchaChallenge({ onValidate, error }) {
  const [challenge, setChallenge] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const refreshChallenge = useCallback(() => {
    setChallenge(generateChallenge());
    setUserAnswer('');
    setIsValid(null);
  }, []);

  useEffect(() => {
    refreshChallenge();
  }, [refreshChallenge]);

  const handleChange = (e) => {
    const value = e.target.value;
    setUserAnswer(value);
    
    if (value === '') {
      setIsValid(null);
      onValidate(false, null);
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && challenge) {
      const valid = numValue === challenge.answer;
      setIsValid(valid);
      
      if (valid) {
        onValidate(true, challenge.token);
      } else {
        onValidate(false, null);
        setAttempts(prev => prev + 1);
        
        // Refresh challenge after 3 wrong attempts
        if (attempts >= 2) {
          setTimeout(() => {
            refreshChallenge();
            setAttempts(0);
          }, 500);
        }
      }
    }
  };

  if (!challenge) return null;

  return (
    <div className="space-y-2">
      <Label className="flex items-center justify-between">
        <span>Vérification anti-spam *</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={refreshChallenge}
          className="h-6 px-2 text-gray-500 hover:text-[#0B6AB3]"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Nouveau
        </Button>
      </Label>
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-100 rounded-lg px-4 py-3 font-mono text-lg text-center select-none">
          {challenge.question}
        </div>
        <Input
          type="number"
          value={userAnswer}
          onChange={handleChange}
          placeholder="?"
          className={`w-24 text-center text-lg font-semibold ${
            isValid === true ? 'border-green-500 bg-green-50' : 
            isValid === false ? 'border-red-500 bg-red-50' : ''
          }`}
          data-testid="captcha-answer"
        />
      </div>
      {isValid === false && (
        <p className="text-sm text-red-500">
          Réponse incorrecte. {attempts >= 2 ? 'Nouveau défi...' : 'Réessayez.'}
        </p>
      )}
      {isValid === true && (
        <p className="text-sm text-green-600">✓ Vérification réussie</p>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
